import { useState, useEffect, useCallback, useRef } from "react";
import type { SoundResponse, TagResponse } from "@wizard-sounds/shared";
import type { SortOption } from "@wizard-sounds/shared";
import { api } from "@/lib/api";

export function useSounds() {
  const [sounds, setSounds] = useState<SoundResponse[]>([]);
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<SortOption>(() => {
    const saved = localStorage.getItem("wizard-sounds-sort") as SortOption;
    if (saved) return saved;
    // Default to popular on mobile, newest on desktop
    return window.innerWidth < 640 ? "popular" : "newest";
  });
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (query === "") {
      setDebouncedQuery("");
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const fetchSounds = useCallback(
    async (reset = false) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.sounds.list({
          q: debouncedQuery || undefined,
          tags: selectedTags.length > 0 ? selectedTags.join(",") : undefined,
          sort,
          limit: 40,
          cursor: reset ? undefined : cursor ?? undefined,
        });
        if (reset) {
          setSounds(res.sounds);
        } else {
          setSounds((prev) => [...prev, ...res.sounds]);
        }
        setCursor(res.cursor);
        setTotal(res.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load sounds");
      } finally {
        setLoading(false);
      }
    },
    [debouncedQuery, selectedTags, sort, cursor],
  );

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.tags.list();
      setTags(res);
    } catch {}
  }, []);

  useEffect(() => {
    fetchSounds(true);
    fetchTags();
  }, [sort, debouncedQuery, selectedTags.join(",")]);

  const handleSortChange = useCallback((newSort: SortOption) => {
    setSort(newSort);
    localStorage.setItem("wizard-sounds-sort", newSort);
  }, []);

  const toggleTag = useCallback((tagSlug: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagSlug)
        ? prev.filter((t) => t !== tagSlug)
        : [...prev, tagSlug],
    );
  }, []);

  const loadMore = useCallback(() => {
    if (cursor) fetchSounds(false);
  }, [cursor, fetchSounds]);

  const incrementPlayCount = useCallback((soundId: string) => {
    setSounds((prev) =>
      prev.map((s) =>
        s.id === soundId ? { ...s, play_count: s.play_count + 1 } : s,
      ),
    );
  }, []);

  return {
    sounds,
    tags,
    loading,
    error,
    total,
    cursor,
    sort,
    query,
    debouncedQuery,
    selectedTags,
    setSort: handleSortChange,
    setQuery,
    toggleTag,
    loadMore,
    incrementPlayCount,
    refetch: () => fetchSounds(true),
  };
}
